/**
 * SSE connection utility for real-time odds updates.
 *
 * Uses fetch + ReadableStream instead of EventSource so we can send
 * the Authorization header required by the backend.
 */

const SSE_PATH = '/odds_screen/stream';

function buildStreamUrl({ league, market, isLive, page, limit }) {
	const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
	const searchParams = new URLSearchParams();

	if (league) searchParams.set('league', league);
	if (market) searchParams.set('market', market);
	if (typeof isLive === 'boolean') {
		searchParams.set('is_live', isLive ? 'true' : 'false');
	}
	if (page != null) searchParams.set('page', String(page));
	if (limit != null) searchParams.set('limit', String(limit));

	return `${baseUrl}${SSE_PATH}?${searchParams.toString()}`;
}

/**
 * Parse a raw SSE text chunk into discrete events.
 *
 * Each SSE event is delimited by a blank line (\n\n). An event looks like:
 *   event: odds_update
 *   data: {"changes":[...]}
 *
 * Returns an array of { event, data } objects where `data` is already
 * JSON-parsed (or the raw string if parsing fails).
 */
function parseSSEChunk(raw) {
	const events = [];
	const blocks = raw.split(/\n\n/);

	for (const block of blocks) {
		const trimmed = block.trim();
		if (!trimmed) continue;

		let eventType = 'message';
		let dataLines = [];

		for (const line of trimmed.split('\n')) {
			if (line.startsWith('event:')) {
				eventType = line.slice('event:'.length).trim();
			} else if (line.startsWith('data:')) {
				dataLines.push(line.slice('data:'.length).trim());
			}
		}

		if (dataLines.length === 0) continue;

		const joined = dataLines.join('\n');
		let parsed;
		try {
			parsed = JSON.parse(joined);
		} catch {
			parsed = joined;
		}

		events.push({ event: eventType, data: parsed });
	}

	return events;
}

/**
 * Open an SSE connection to the odds stream endpoint.
 *
 * @param {Object} opts
 * @param {string}  [opts.league]
 * @param {string}  [opts.market]
 * @param {boolean} [opts.isLive]
 * @param {number}  [opts.page]
 * @param {number}  [opts.limit]
 * @param {string}  [opts.accessToken]
 * @param {Function} [opts.onConnected]  — called once the stream is open
 * @param {Function} [opts.onUpdate]     — called with parsed event data for each odds_update
 * @param {Function} [opts.onError]      — called with an Error on failure
 *
 * @returns {Function} disconnect — call to abort the stream
 */
export function connectOddsStream({
	league,
	market,
	isLive,
	page,
	limit,
	accessToken,
	onConnected,
	onUpdate,
	onError,
} = {}) {
	const controller = new AbortController();

	const url = buildStreamUrl({ league, market, isLive, page, limit });

	const headers = { Accept: 'text/event-stream' };
	if (accessToken) {
		headers.Authorization = `Bearer ${accessToken}`;
	}

	(async () => {
		try {
			const response = await fetch(url, {
				method: 'GET',
				headers,
				signal: controller.signal,
			});

			if (!response.ok) {
				throw new Error(`SSE stream responded with ${response.status} ${response.statusText}`);
			}

			onConnected?.();

			const reader = response.body.getReader();
			const decoder = new TextDecoder();
			let buffer = '';

			while (true) {
				const { done, value } = await reader.read();
				if (done) break;

				buffer += decoder.decode(value, { stream: true });

				// SSE events are separated by double newlines; only process
				// complete events, keeping any trailing partial in the buffer.
				const lastDoubleNewline = buffer.lastIndexOf('\n\n');
				if (lastDoubleNewline === -1) continue;

				const complete = buffer.slice(0, lastDoubleNewline + 2);
				buffer = buffer.slice(lastDoubleNewline + 2);

				const events = parseSSEChunk(complete);
				for (const evt of events) {
					if (evt.event === 'odds_update') {
						onUpdate?.(evt.data);
					}
				}
			}
		} catch (err) {
			if (err.name === 'AbortError') return;
			onError?.(err);
		}
	})();

	return function disconnect() {
		controller.abort();
	};
}
